import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const initialGroups = [
  { id: 1, name: "Marketing Team", members: 8, status: "Active" },
  { id: 2, name: "Engineering", members: 12, status: "Inactive" },
  { id: 3, name: "Support", members: 6, status: "Active" },
  { id: 4, name: "Executives", members: 3, status: "Inactive" },
];

export function Groups() {
  const [groups, setGroups] = useState(initialGroups);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [isActive, setActive] = useState(true);

  // Filter groups by active/inactive status
  useEffect(() => {
    const filtered = initialGroups.filter((g) =>
      isActive ? g.status === "Active" : g.status !== "Active"
    );
    setGroups(filtered);
    setSelectedGroups(new Set()); // reset selection when filter changes
  }, [isActive]);

  const toggleGroup = (id) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedGroups.size === groups.length) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(groups.map((group) => group.id)));
    }
  };

  const handleDeleteSelected = () => {
    setGroups((prev) => prev.filter((group) => !selectedGroups.has(group.id)));
    setSelectedGroups(new Set());
  };

  const isAllSelected = groups.length > 0 && selectedGroups.size === groups.length;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              Groups
            </h2>
            <Link
              to="/groups/create"
              className="flex text-indigo-500 h-8 w-8 items-center justify-center rounded-full hover:bg-indigo-600 hover:text-white text-lg "
              title="Create Group"
            >
              <i className="fas fa-plus"></i>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <a
              onClick={() => setActive(!isActive)}
              className="flex items-center gap-1 px-3 py-2 text-indigo-500 hover:text-indigo-600 text-sm font-medium cursor-pointer select-none"
            >
              <i
                className={`fa-solid ${isActive ? "fa-user-slash text-xl" : "fa-user text-xl"
                }`}
              ></i>
              {isActive ? "Show Inactive" : "Show Active"}
            </a>

            <button
              onClick={handleDeleteSelected}
              className="text-indigo-500 hover:text-indigo-600 text-lg cursor-pointer"
              title="Delete selected groups"
            >
              <i className={`fa-solid ${!isActive ? 'fa-trash-arrow-up text-xl' : 'fa-trash text-xl'}`}></i>
            </button>
          </div>
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-12 px-6 py-3 text-center">
                <i
                  className={`far ${
                    isAllSelected ? "fa-check-square" : "fa-square"
                  } text-indigo-500 text-lg cursor-pointer`}
                  onClick={toggleSelectAll}
                  title="Select all groups"
                ></i>
              </th>
              <th className="px-6 py-3 text-left font-medium text-xs text-gray-500 uppercase tracking-wider">
                Group Name
              </th>
              <th className="px-6 py-3 text-left font-medium text-xs text-gray-500 uppercase tracking-wider">
                Members
              </th>
              <th className="px-6 py-3 text-left font-medium text-xs text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groups.map((group) => {
              const isSelected = selectedGroups.has(group.id);
              return (
                <tr
                  key={group.id}
                  className={`hover:bg-indigo-50 ${isSelected ? "bg-indigo-100" : ""}`}
                >
                  <td className="w-12 px-6 py-4 text-center text-indigo-500 text-lg">
                    <i
                      className={`far ${
                        isSelected ? "fa-check-square" : "fa-square"
                      } cursor-pointer`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(group.id);
                      }}
                    ></i>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {group.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {group.members}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Link
                      to={`/groups/edit/${group.id}`}
                      className=" text-indigo-500 hover:text-indigo-600"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
